import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import dayjs from 'dayjs';
import Loading from '$/components/loading/Loading';
import { PlannerHealthService } from '$/services/PlannerHealthService';
import HealthNavigation from '$/pages/admin/health/HealthNavigation';
import * as Bixby from '$/core';

const PlannerHealth: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [error, setError] = useState(null as string | null);
  const [plannerHealthResults, setPlannerHealthResults] = useState(null as Bixby.Models.PlannerHealthResult | null);

  useEffect(() => {
    const getPlannerHealthResults = async () => {
      setIsLoading(true);
      try {
        const results: Bixby.Models.PlannerHealthResult = await PlannerHealthService.getPlannerHealth();
        setPlannerHealthResults(results);
      } catch {
        setError('Error retrieving Microsoft Planner health results.');
      } finally {
        setIsLoading(false);
      }
    };
    getPlannerHealthResults();
  }, []);

  const calculatePlannerHealthResults = async () => {
    setIsRecalculating(true);
    try {
      const results: Bixby.Models.PlannerHealthResult = await PlannerHealthService.analyzePlannerDataAsync();
      setPlannerHealthResults(results);
    } catch {
      setError('Error calculating Microsoft Planner health results.');
    } finally {
      setIsRecalculating(false);
    }
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <HealthNavigation>
        <div className="health-container-wrapper">
          <div className="health-header">
            <h2>
              Last Run:{' '}
              {plannerHealthResults ? dayjs(plannerHealthResults.lastRun).format('MMMM D, YYYY h:mm A') : 'N/A'}
            </h2>
            <ul>
              <li>Maximum tasks in a plan: 9,000</li>
              <li>Maximum active tasks in a plan: 3,000</li>
              <li>Maximum tasks assigned to a user: 9,000</li>
            </ul>
          </div>

          <div className="health-actions">
            <button type="button" disabled={isRecalculating} onClick={calculatePlannerHealthResults}>
              Recalculate
            </button>
          </div>

          <h2>Results</h2>

          <div className={classnames('health-container', { recalculating: isRecalculating })}>
            {error && <div className="error-message">{error}</div>}
            {plannerHealthResults && (
              <>
                <div className="health-column">
                  <ul>
                    {(plannerHealthResults.planResults || [])
                      .sort((a, b) => (a.totalTaskCount > b.totalTaskCount ? -1 : 1))
                      .map((planResult, planIndex) => {
                        return (
                          <li key={planIndex}>
                            {planResult.planTitle}
                            <ul>
                              <li className={classnames({ warn: planResult.totalTaskCount > 8000 })}>
                                Total: {planResult.totalTaskCount}
                              </li>
                              <li className={classnames({ warn: planResult.totalActiveTaskCount > 2000 })}>
                                Active: {planResult.totalActiveTaskCount}
                              </li>
                              {planResult.buckets && planResult.buckets.length > 0 && (
                                <li>
                                  Buckets
                                  <table>
                                    <thead>
                                      <tr>
                                        <th>Bucket Name</th>
                                        <th>Total</th>
                                        <th>Active</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {planResult.buckets
                                        .sort((a, b) => (a.bucketName > b.bucketName ? -1 : 1))
                                        .map((bucket, bucketIndex) => {
                                          return (
                                            <tr key={bucketIndex}>
                                              <td>{bucket.bucketName}</td>
                                              <td className={classnames({ warn: bucket.totalTaskCount > 8000 })}>
                                                {bucket.totalTaskCount}
                                              </td>
                                              <td className={classnames({ warn: bucket.totalActiveTaskCount > 2000 })}>
                                                {bucket.totalActiveTaskCount}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </table>
                                </li>
                              )}
                            </ul>
                          </li>
                        );
                      })}
                  </ul>
                </div>
                <div className="health-column">
                  <ul>
                    {plannerHealthResults.userResults
                      .sort((a, b) => (a.totalTaskCount > b.totalTaskCount ? -1 : 1))
                      .map((userResult, index) => {
                        return (
                          <li key={index}>
                            {userResult.displayName}
                            <ul>
                              <li className={classnames({ warn: userResult.totalTaskCount > 8000 })}>
                                Total: {userResult.totalTaskCount}
                              </li>
                              <li className={classnames({ warn: userResult.totalTaskCount > 8000 })}>
                                Active: {userResult.totalActiveTaskCount}
                              </li>
                            </ul>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </HealthNavigation>
    );
  }
};

export default PlannerHealth;
